import React from 'react';
import { createPortal } from 'react-dom';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';

const OVERLAY_STYLE = {
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  inset: 0,
  justifyContent: 'center',
  position: 'fixed',
  zIndex: 9999
};

const INLINE_STYLE = {
  alignItems: 'center',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: '100%'
};

const Loader = ({ message = '', loaderSize = 56, fullScreen = true }) => {
  const theme = useTheme();

  const loaderColor = theme?.palette?.primary?.main ?? '#6366F1';
  const displayMessage = message || (fullScreen ? 'Loading...' : '');
  const isDark = theme?.palette?.mode === 'dark';

  const content = (
    <>
      <CircularProgress
        aria-label="loading"
        size={loaderSize}
        sx={{ color: loaderColor }}
        thickness={4}
      />
      {displayMessage ? (
        <Typography
          component="p"
          sx={{
            color: theme?.palette?.text?.primary ?? '#334155',
            fontSize: 16,
            fontWeight: 500,
            m: 0,
            mt: 2,
            textAlign: 'center'
          }}
        >
          {displayMessage}
        </Typography>
      ) : null}
    </>
  );

  if (fullScreen) {
    const overlay = (
      <div
        aria-busy="true"
        aria-live="polite"
        role="progressbar"
        style={{
          ...OVERLAY_STYLE,
          backgroundColor: isDark ? 'rgba(0,0,0,0.65)' : 'rgba(255,255,255,0.88)'
        }}
      >
        {content}
      </div>
    );

    if (typeof document !== 'undefined') {
      return createPortal(overlay, document.body);
    }

    return overlay;
  }

  return (
    <div
      aria-busy="true"
      aria-live="polite"
      role="progressbar"
      style={{
        ...INLINE_STYLE,
        minHeight: Math.max(120, loaderSize + (displayMessage ? 36 : 0)),
        padding: '24px 0'
      }}
    >
      {content}
    </div>
  );
};

export default Loader;
